<script setup>
import { ref, computed, nextTick, onBeforeUnmount } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import { guardarModal } from 'dashboard/directives/guardaDoModal';

const props = defineProps({
  type: {
    type: String,
    default: 'edit',
    validator: value => ['alert', 'edit'].includes(value),
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  // Classe do icone da faixa (`i-ph-trash`). Sem ela a faixa vem so
  // com o titulo.
  icone: {
    type: String,
    default: '',
  },
  // A linha de baixo do título, como nos modais `.advup-modal` (o "Fica na
  // ficha do contato, visível só para a equipe" da nota). Opcional.
  subtitulo: {
    type: String,
    default: '',
  },
  cancelButtonLabel: {
    type: String,
    default: '',
  },
  confirmButtonLabel: {
    type: String,
    default: '',
  },
  disableConfirmButton: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  showCancelButton: {
    type: Boolean,
    default: true,
  },
  showConfirmButton: {
    type: Boolean,
    default: true,
  },
  overflowYAuto: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: 'lg',
    validator: value => ['3xl', '2xl', 'xl', 'lg', 'md', 'sm'].includes(value),
  },
  position: {
    type: String,
    default: 'center',
    validator: value => ['center', 'top'].includes(value),
  },
});

const emit = defineEmits(['confirm', 'close']);

const { t } = useI18n();

const dialogRef = ref(null);
const dialogContentRef = ref(null);
const isOpen = ref(false);

// `alert` encerra ou destroi -> rubi. `edit` mexe em conteudo -> azul, que e
// o tom padrao da faixa.
const tomDoTopo = computed(() =>
  props.type === 'alert' ? 'advup-modal__topo--rubi' : ''
);

const maxWidthClass = computed(() => {
  const classesMap = {
    '3xl': 'max-w-3xl',
    '2xl': 'max-w-2xl',
    xl: 'max-w-xl',
    lg: 'max-w-lg',
    md: 'max-w-md',
    sm: 'max-w-sm',
  };

  return classesMap[props.width] ?? 'max-w-md';
});

const positionClass = computed(() =>
  props.position === 'top' ? 'dialog-position-top' : ''
);

// O DIÁLOGO SEGUE A TELA (05/10/2026). Ele é teleportado para o `body`, fora
// do escopo `.advup-modais-claros` que a tela redesenhada põe na raiz — e por
// isso abria com o degradê escuro antigo no meio de uma tela de topos claros
// (o "Excluir" de Casos, Funil, Financeiro…). Na hora de abrir ele pergunta
// se a tela visível adotou o topo claro e veste o mesmo.
const claro = ref(false);
// Sem ícone próprio, o alerta claro ganha o aviso: no topo claro o selo é a
// âncora do olho, e faixa clara só com título parece cabeçalho de página.
const iconeDoSelo = computed(
  () =>
    props.icone || (claro.value && props.type === 'alert' ? 'i-ph-warning' : '')
);

// A GUARDA (05/10/2026). A guarda do modal (`directives/guardaDoModal.js`)
// chegou aos modais `.advup-modal` e `woot-modal`, mas esta casca fechava no
// clique fora e no Escape sem olhar o que estava escrito — "Novo contato"
// preenchido, um clique fora, tudo perdido. Agora vale a mesma regra: com
// algo escrito, clique fora faz o modal tremer; Escape, X e Cancelar
// perguntam "Descartar o que você escreveu?" dentro dele.
let guarda = null;

const soltarGuarda = () => {
  guarda?.soltar();
  guarda = null;
};

// A SAÍDA PELO MESMO CAMINHO (27/10/2026). O `<dialog>` nativo chegava subindo e sumia seco
// ao fechar — `close()` tira a folha da tela no mesmo quadro, e o `<Transition>` do Vue não
// alcança o que o navegador esconde. Agora a folha desce e esmaece (`advupModalSai`, a mesma
// das outras cascas) e o véu apaga; só no fim da animação o `<dialog>` fecha de verdade. Com
// "reduzir movimento" o token vira 0,01 s; se a animação não terminar, um teto fecha assim mesmo.
const saindo = ref(false);
let saida = 0;

const fecharDeVez = vez => {
  if (vez !== saida) return;
  saindo.value = false;
  isOpen.value = false;
  dialogRef.value?.close();
};

const sairDevagar = () => {
  const folha = dialogContentRef.value;
  saida += 1;
  const vez = saida;
  saindo.value = true;
  folha?.addEventListener(
    'animationend',
    evento => evento.animationName === 'advupModalSai' && fecharDeVez(vez),
    { once: true }
  );
  setTimeout(() => fecharDeVez(vez), 500);
};

const open = () => {
  saida += 1;
  saindo.value = false;
  claro.value = Boolean(document.querySelector('.advup-modais-claros'));
  isOpen.value = true;
  dialogRef.value?.showModal();
  // O `showModal` foca o primeiro controle — às vezes um botão do rodapé — e
  // rola a folha até ele: o Novo contato abria com o título cortado no
  // celular (06/10/2026). A folha sempre começa do topo.
  nextTick(() => {
    if (dialogRef.value) dialogRef.value.scrollTop = 0;
  });
  soltarGuarda();
  if (dialogContentRef.value) {
    guarda = guardarModal(dialogContentRef.value, { t });
  }
};

const close = () => {
  soltarGuarda();
  emit('close');
  // O conteúdo (`<slot v-if="isOpen">`) fica até o fim da saída: sumir antes encolhia a folha.
  if (dialogRef.value?.open) {
    if (!saindo.value) sairDevagar();
  } else {
    isOpen.value = false;
  }
};

// Escape no <dialog> nativo chega como `cancel`, antes de fechar.
const handleCancel = evento => {
  if (evento.target !== dialogRef.value) return;
  // O Escape também sai pelo caminho de entrada: o navegador não fecha, quem fecha é `close`.
  evento.preventDefault();
  if (guarda?.aoEscape(close)) return;
  close();
};

onBeforeUnmount(soltarGuarda);

// Only close if the close event originated from this dialog,
// not from a child dialog (e.g. ProseMirror prompt) bubbling up.
const handleDialogClose = e => e.target === dialogRef.value && close();

// Only close on click-outside if this dialog is the topmost one.
// If another dialog (e.g. ProseMirror prompt) is open on top, ignore.
const handleClickOutside = () => {
  const dialogs = document.querySelectorAll('dialog[open]');
  if (dialogs[dialogs.length - 1] !== dialogRef.value) return;
  // Com algo escrito não fecha: a guarda já fez o modal tremer.
  if (guarda?.sujo()) return;
  close();
};

const confirm = () => {
  emit('confirm');
};

defineExpose({ open, close });
</script>

<template>
  <TeleportWithDirection to="body">
    <dialog
      ref="dialogRef"
      class="w-full shadow-xl rounded-xl"
      :class="[
        { 'dlg--saindo': saindo },
        maxWidthClass,
        positionClass,
        overflowYAuto ? 'overflow-y-auto' : 'overflow-visible',
      ]"
      @close.prevent="handleDialogClose"
      @cancel="handleCancel"
    >
      <OnClickOutside @trigger="handleClickOutside">
        <form
          ref="dialogContentRef"
          class="dlg-folha flex flex-col w-full h-auto gap-6 p-6 overflow-visible text-start align-middle transform"
          :class="{ 'advup-modal--claro': claro }"
          @submit.prevent="confirm"
          @click.stop
        >
          <!-- Terceira casca de modal do produto (as outras sao
               `.advup-modal` e `woot-modal`). Ela abria com o titulo preto
               sobre a mesma folha do corpo, sem nada marcando onde o modal
               comeca — do lado de um `woot-modal` parecia de outro produto.
               Agora usa a MESMA faixa colorida: `alert` em rubi (encerra ou
               destroi), `edit` em azul. O recuo negativo faz a faixa ir de
               ponta a ponta sem tirar o `p-6` do formulario, que todo slot
               deste componente ja conta que existe. -->
          <div
            v-if="title"
            class="advup-modal__topo dlg-topo"
            :class="tomDoTopo"
          >
            <span v-if="iconeDoSelo" class="advup-modal__selo">
              <i :class="iconeDoSelo" />
            </span>
            <div v-if="subtitulo" class="min-w-0">
              <h3 class="advup-modal__titulo">{{ title }}</h3>
              <p class="advup-modal__sub advup-modal__sub--solta">
                {{ subtitulo }}
              </p>
            </div>
            <h3 v-else class="advup-modal__titulo">{{ title }}</h3>
          </div>
          <div v-if="description || $slots.description" class="flex flex-col">
            <slot name="description">
              <p v-if="description" class="mb-0 text-sm text-n-slate-11">
                {{ description }}
              </p>
            </slot>
          </div>
          <slot v-if="isOpen" />
          <!-- Dialog content will be injected here -->
          <slot name="footer">
            <div
              v-if="showCancelButton || showConfirmButton"
              class="flex items-center justify-between w-full gap-3"
            >
              <Button
                v-if="showCancelButton"
                variant="faded"
                color="slate"
                :label="cancelButtonLabel || t('DIALOG.BUTTONS.CANCEL')"
                class="w-full"
                type="button"
                @click="close"
              />
              <Button
                v-if="showConfirmButton"
                :color="type === 'edit' ? 'blue' : 'ruby'"
                :label="confirmButtonLabel || t('DIALOG.BUTTONS.CONFIRM')"
                class="w-full"
                :is-loading="isLoading"
                :disabled="disableConfirmButton || isLoading"
                type="submit"
              />
            </div>
          </slot>
        </form>
      </OnClickOutside>
    </dialog>
  </TeleportWithDirection>
</template>

<style scoped>
/* Mesmo veu de `.advup-modal-fundo` e do `woot-modal`: navy a 55% com
   desfoque. Era `bg-n-alpha-black1`, quase transparente. */
dialog::backdrop {
  background: rgb(15 23 42 / 0.55);
  backdrop-filter: blur(3px);
}

/* Mesma superficie, mesmo raio, mesma sombra e o mesmo tempero de entrada das
   outras duas cascas. Era `bg-n-alpha-3` — translucida, com a tela vazando
   por tras. */
.dlg-folha {
  /* `clip` corta igual a `hidden`, mas não vira caixa de rolagem: assim um
     rodapé `sticky` dentro do formulário gruda no fundo do <dialog> que rola
     (o Novo contato no celular deixava "Salvar" abaixo da dobra). */
  overflow: hidden;
  overflow: clip;
  /* Sem fio claro por cima da faixa navy — a mesma decisão da peça
     `.advup-modal` (14/09/2026): transparente no claro, `slate-4` no escuro. */
  border: 1px solid transparent;
  border-radius: var(--r-5);
  background: rgb(var(--solid-1));
  /* Borda transparente sem isto mostra o fundo branco da própria folha. */
  background-clip: padding-box;
  box-shadow:
    0 4px 12px rgb(15 23 42 / 0.12),
    0 24px 60px -20px rgb(15 23 42 / 0.45);
  animation: advupModalChega var(--mov-2) var(--mov-curva);
}

.dark .dlg-folha {
  border-color: rgb(var(--slate-4));
  box-shadow:
    0 4px 12px rgb(0 0 0 / 0.5),
    0 24px 60px -20px rgb(0 0 0 / 0.8);
}

/* Sangra o `p-6` do formulario para a faixa ir de ponta a ponta. */
.dlg-topo {
  /* Nao puxa a margem de baixo: o formulario tem `gap-6`, e cancelar esse
     espaco cola a descricao na faixa. */
  margin: calc(var(--esp-6) * -1) calc(var(--esp-6) * -1) 0;
}

@media (prefers-reduced-motion: reduce) {
  .dlg-folha {
    animation: none;
  }
}

/* A saída: mais específica que a chegada (e que o `none` de cima), para rodar também com
   "reduzir movimento" — em 0,01 s, só para o `animationend` acontecer. */
.dlg--saindo .dlg-folha {
  pointer-events: none;
  animation: advupModalSai var(--mov-2) var(--mov-curva-saida) forwards;
}

.dlg--saindo::backdrop {
  animation: advupModalFundoSai var(--mov-2) var(--mov-curva-saida) forwards;
}

.dialog-position-top {
  margin-top: clamp(2rem, 5vh, 5rem);
  margin-bottom: auto;
}
</style>
